import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search,
  X,
  Clock,
  Filter,
  FileText,
  AlertCircle,
  Loader2,
  MapPin,
  Calendar,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { semanticSearch, transformSearchResult } from '../../services/api';

// Types
export interface SearchResult {
  id: string;
  text: string;
  contractId: string;
  contractName: string;
  contractState: string;
  articleNumber: string;
  articleTitle: string;
  sectionNumber: string;
  sectionTitle: string;
  clauseNumber: string;
  similarity: number;
  highlights: { start: number; end: number }[];
  cfrCitation?: string;
  createdAt?: string;
}

export interface SearchFilters {
  states: string[];
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

export interface SearchHistoryItem {
  id: string;
  query: string;
  timestamp: string;
  resultCount: number;
}

export interface NaturalLanguageSearchProps {
  onSearch?: (query: string, filters: SearchFilters) => Promise<SearchResult[]>;
  onResultClick?: (result: SearchResult) => void;
  onViewInContext?: (result: SearchResult) => void;
  availableStates?: string[];
  initialResults?: SearchResult[];
  testId?: string;
}

// Suggested queries for auto-complete
const SUGGESTED_QUERIES = [
  'prior authorization timelines for behavioral health',
  'emergency services without prior authorization',
  'MHPAEA compliance requirements',
  'network adequacy standards for specialty care',
  'grievance and appeal procedures',
  'EPSDT coverage for pediatric services',
  'concurrent review clinical guidelines',
  'provider credentialing requirements',
  'MLR calculation methodology',
  'enrollment verification procedures',
];

// Local storage key for search history
const SEARCH_HISTORY_KEY = 'medicaid-search-history';

// Helper to highlight matched text
const highlightText = (text: string, highlights: { start: number; end: number }[]): React.ReactNode => {
  if (!highlights || highlights.length === 0) {
    return text;
  }

  // Sort highlights by start position
  const sortedHighlights = [...highlights].sort((a, b) => a.start - b.start);

  const parts: React.ReactNode[] = [];
  let lastEnd = 0;

  sortedHighlights.forEach((highlight, index) => {
    // Add text before this highlight
    if (highlight.start > lastEnd) {
      parts.push(
        <span key={`text-${index}`}>{text.slice(lastEnd, highlight.start)}</span>
      );
    }
    // Add highlighted text
    parts.push(
      <mark
        key={`highlight-${index}`}
        className="bg-yellow-200 text-yellow-900 px-0.5 rounded"
      >
        {text.slice(highlight.start, highlight.end)}
      </mark>
    );
    lastEnd = highlight.end;
  });

  // Add remaining text
  if (lastEnd < text.length) {
    parts.push(<span key="text-end">{text.slice(lastEnd)}</span>);
  }

  return <>{parts}</>;
};

// Format similarity score as percentage
const formatSimilarity = (similarity: number): string => {
  return `${Math.round(similarity * 100)}%`;
};

// State abbreviation to full name mapping
const STATE_NAMES: Record<string, string> = {
  PR: 'Puerto Rico',
  FL: 'Florida',
  TX: 'Texas',
  CA: 'California',
  NY: 'New York',
};

export const NaturalLanguageSearch: React.FC<NaturalLanguageSearchProps> = ({
  onSearch,
  onResultClick,
  onViewInContext,
  availableStates = ['PR', 'FL', 'TX', 'CA', 'NY'],
  initialResults = [],
  testId = 'natural-language-search',
}) => {
  // State
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>(initialResults);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [filters, setFilters] = useState<SearchFilters>({
    states: [],
    dateRange: { start: null, end: null },
  });
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Load search history from localStorage on mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(SEARCH_HISTORY_KEY);
      if (savedHistory) {
        setSearchHistory(JSON.parse(savedHistory));
      }
    } catch (e) {
      console.error('Failed to load search history:', e);
    }
  }, []);

  // Save search history to localStorage
  const saveSearchHistory = useCallback((history: SearchHistoryItem[]) => {
    try {
      localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history.slice(0, 10)));
      setSearchHistory(history.slice(0, 10));
    } catch (e) {
      console.error('Failed to save search history:', e);
    }
  }, []);

  // Filter suggestions based on query
  const filteredSuggestions = useMemo(() => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    return SUGGESTED_QUERIES.filter((suggestion) =>
      suggestion.toLowerCase().includes(lowerQuery)
    ).slice(0, 5);
  }, [query]);

  // Handle clicking outside suggestions dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Perform search
  const handleSearch = useCallback(async (searchQuery: string = query) => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);
    setShowSuggestions(false);
    setShowHistory(false);

    try {
      let searchResults: SearchResult[];

      if (onSearch) {
        // Use custom search handler if provided
        searchResults = await onSearch(searchQuery, filters);
      } else {
        // Use the real semantic search API
        const stateFilter = filters.states.length === 1 ? filters.states[0] : undefined;
        const response = await semanticSearch({
          query: searchQuery,
          state: stateFilter,
          matchCount: 20,
          similarityThreshold: 0.5,
        });

        // Transform API results to frontend format
        searchResults = response.results.map((result) =>
          transformSearchResult(result, searchQuery)
        );

        // Apply multi-state filter (API only supports single state, so filter multiple here)
        if (filters.states.length > 1) {
          searchResults = searchResults.filter((r) =>
            filters.states.includes(r.contractState)
          );
        }
      }

      // Apply date range filter (client-side only)
      if (filters.dateRange.start) {
        searchResults = searchResults.filter(
          (r) => !r.createdAt || r.createdAt >= filters.dateRange.start!
        );
      }
      if (filters.dateRange.end) {
        searchResults = searchResults.filter(
          (r) => !r.createdAt || r.createdAt <= filters.dateRange.end!
        );
      }

      setResults(searchResults);

      // Add to search history
      const historyItem: SearchHistoryItem = {
        id: Date.now().toString(),
        query: searchQuery,
        timestamp: new Date().toISOString(),
        resultCount: searchResults.length,
      };
      const newHistory = [historyItem, ...searchHistory.filter((h) => h.query !== searchQuery)];
      saveSearchHistory(newHistory);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Failed to perform search';
      setError(errorMessage);
      console.error('Search error:', e);
    } finally {
      setIsLoading(false);
    }
  }, [query, filters, onSearch, searchHistory, saveSearchHistory]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim()) {
      setShowSuggestions(true);
      setShowHistory(false);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (query.trim()) {
      setShowSuggestions(true);
    } else if (searchHistory.length > 0) {
      setShowHistory(true);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setShowHistory(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    handleSearch(suggestion);
  };

  // Handle history item click
  const handleHistoryClick = (historyItem: SearchHistoryItem) => {
    setQuery(historyItem.query);
    setShowHistory(false);
    handleSearch(historyItem.query);
  };

  // Clear search history
  const handleClearHistory = () => {
    saveSearchHistory([]);
    setShowHistory(false);
  };

  // Handle state filter toggle
  const handleStateToggle = (state: string) => {
    setFilters((prev) => ({
      ...prev,
      states: prev.states.includes(state)
        ? prev.states.filter((s) => s !== state)
        : [...prev.states, state],
    }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      states: [],
      dateRange: { start: null, end: null },
    });
  };

  // Handle result selection
  const handleResultSelect = (result: SearchResult) => {
    setSelectedResult(result);
    onResultClick?.(result);
  };

  const hasActiveFilters = filters.states.length > 0 || filters.dateRange.start || filters.dateRange.end;

  return (
    <div className="space-y-6" data-testid={testId}>
      {/* Search Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4" data-testid={`${testId}-title`}>
          Natural Language Search
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Search across all contracts using natural language queries. Find specific clauses, requirements, or regulatory citations.
        </p>

        {/* Search Input */}
        <div className="relative">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isLoading ? (
                  <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onKeyDown={handleKeyDown}
                placeholder="Enter your search query, e.g., 'prior authorization timelines for behavioral health'"
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                data-testid={`${testId}-input`}
              />
              {query && (
                <button
                  onClick={() => {
                    setQuery('');
                    setResults([]);
                    setSelectedResult(null);
                    searchInputRef.current?.focus();
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  data-testid={`${testId}-clear`}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <button
              onClick={() => handleSearch()}
              disabled={isLoading || !query.trim()}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              data-testid={`${testId}-button`}
            >
              Search
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 border rounded-lg transition-colors ${
                hasActiveFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`${testId}-filter-toggle`}
            >
              <Filter className="h-5 w-5" />
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
              data-testid={`${testId}-suggestions`}
            >
              <div className="p-2">
                <div className="text-xs text-gray-500 px-3 py-1">Suggestions</div>
                {filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center gap-2"
                    data-testid={`${testId}-suggestion-${index}`}
                  >
                    <Search className="h-4 w-4 text-gray-400" />
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search History Dropdown */}
          {showHistory && searchHistory.length > 0 && (
            <div
              ref={suggestionsRef}
              className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
              data-testid={`${testId}-history`}
            >
              <div className="p-2">
                <div className="flex items-center justify-between px-3 py-1">
                  <span className="text-xs text-gray-500">Recent Searches</span>
                  <button
                    onClick={handleClearHistory}
                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    data-testid={`${testId}-clear-history`}
                  >
                    <Trash2 className="h-3 w-3" />
                    Clear
                  </button>
                </div>
                {searchHistory.slice(0, 5).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleHistoryClick(item)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center justify-between"
                    data-testid={`${testId}-history-item-${item.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="truncate max-w-md">{item.query}</span>
                    </span>
                    <span className="text-xs text-gray-400">{item.resultCount} results</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div
            className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
            data-testid={`${testId}-filters`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Filters</h3>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="text-xs text-red-500 hover:text-red-700"
                  data-testid={`${testId}-clear-filters`}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* State Filter */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2 flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                States
              </label>
              <div className="flex flex-wrap gap-2">
                {availableStates.map((state) => (
                  <button
                    key={state}
                    onClick={() => handleStateToggle(state)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      filters.states.includes(state)
                        ? 'bg-primary-100 border-primary-500 text-primary-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                    }`}
                    data-testid={`${testId}-filter-state-${state}`}
                  >
                    {state} - {STATE_NAMES[state] || state}
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Filter */}
            <div>
              <label className="block text-sm text-gray-600 mb-2 flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Date Range
              </label>
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="date"
                    value={filters.dateRange.start || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, start: e.target.value || null },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    data-testid={`${testId}-filter-date-start`}
                  />
                </div>
                <span className="text-gray-400 self-center">to</span>
                <div className="flex-1">
                  <input
                    type="date"
                    value={filters.dateRange.end || ''}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, end: e.target.value || null },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    data-testid={`${testId}-filter-date-end`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3"
          data-testid={`${testId}-error`}
        >
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results Section */}
      {results.length > 0 && (
        <div className="bg-white rounded-lg shadow-md" data-testid={`${testId}-results`}>
          {/* Results Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                {results.length} result{results.length !== 1 ? 's' : ''} found
              </h3>
              <div className="text-xs text-gray-500">
                Sorted by relevance
              </div>
            </div>
          </div>

          {/* Results List */}
          <div className="divide-y divide-gray-200">
            {results.map((result) => (
              <div
                key={result.id}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                  selectedResult?.id === result.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                }`}
                onClick={() => handleResultSelect(result)}
                data-testid={`${testId}-result-${result.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Result Header */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {result.contractState}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {result.contractName}
                      </span>
                      <span className="text-xs text-gray-400">
                        Art. {result.articleNumber}, Sec. {result.sectionNumber}, Cl. {result.clauseNumber}
                      </span>
                    </div>

                    {/* Result Text with Highlights */}
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {highlightText(result.text, result.highlights)}
                    </p>

                    {/* Result Metadata */}
                    <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {result.articleTitle}
                      </span>
                      {result.cfrCitation && (
                        <span className="text-primary-600">{result.cfrCitation}</span>
                      )}
                    </div>
                  </div>

                  {/* Similarity Score */}
                  <div className="flex flex-col items-end gap-2">
                    <div
                      className={`text-sm font-medium px-2 py-1 rounded ${
                        result.similarity >= 0.9
                          ? 'bg-green-100 text-green-700'
                          : result.similarity >= 0.8
                          ? 'bg-blue-100 text-blue-700'
                          : result.similarity >= 0.7
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                      data-testid={`${testId}-result-${result.id}-similarity`}
                    >
                      {formatSimilarity(result.similarity)} match
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Result Detail Panel */}
      {selectedResult && (
        <div
          className="bg-white rounded-lg shadow-md p-6"
          data-testid={`${testId}-detail-panel`}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Clause Detail</h3>
              <p className="text-sm text-gray-500">
                {selectedResult.contractName} - Article {selectedResult.articleNumber}
              </p>
            </div>
            <button
              onClick={() => setSelectedResult(null)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Source Attribution */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Source Location</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Contract:</span>
                <span className="ml-2 text-gray-900">{selectedResult.contractName}</span>
              </div>
              <div>
                <span className="text-gray-500">State:</span>
                <span className="ml-2 text-gray-900">
                  {selectedResult.contractState} - {STATE_NAMES[selectedResult.contractState]}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Article:</span>
                <span className="ml-2 text-gray-900">
                  {selectedResult.articleNumber}. {selectedResult.articleTitle}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Section:</span>
                <span className="ml-2 text-gray-900">
                  {selectedResult.sectionNumber}. {selectedResult.sectionTitle}
                </span>
              </div>
              {selectedResult.cfrCitation && (
                <div className="col-span-2">
                  <span className="text-gray-500">Regulatory Citation:</span>
                  <span className="ml-2 text-primary-600 font-medium">{selectedResult.cfrCitation}</span>
                </div>
              )}
            </div>
          </div>

          {/* Full Clause Text */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Full Clause Text</h4>
            <p className="text-sm text-gray-800 bg-white border border-gray-200 rounded-lg p-4">
              {selectedResult.text}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => onViewInContext?.(selectedResult)}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              data-testid={`${testId}-view-in-context`}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View in Contract
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && query && results.length === 0 && !error && (
        <div
          className="bg-white rounded-lg shadow-md p-12 text-center"
          data-testid={`${testId}-empty`}
        >
          <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-4">
            Try adjusting your search query or filters to find what you're looking for.
          </p>
          <div className="text-sm text-gray-400">
            <p className="mb-2">Suggestions:</p>
            <ul className="space-y-1">
              <li>Use more general terms</li>
              <li>Check for spelling errors</li>
              <li>Remove some filters</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default NaturalLanguageSearch;
